import { queryOptions, useQuery } from '@tanstack/react-query';

import type { DateTime, TimeRange } from '@cast/shared-types';
import { WorkloadLabelFilters, WorkloadMetadata } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_METADATA_QUERY_KEY = 'workloads-metadata';

const getWorkloadsMetadataQueryKey = ({
  startTime,
  endTime,
  filter,
  useListingPrices,
}: {
  startTime?: string;
  endTime?: string;
  filter?: string;
  useListingPrices?: boolean;
} = {}) =>
  [
    WORKLOADS_METADATA_QUERY_KEY,
    ...(startTime !== undefined
      ? [startTime, endTime, filter, useListingPrices]
      : []),
  ] as const;

type UseWorkloadsMetadataQueryArgs = {
  range: TimeRange<DateTime>;
  filter?: WorkloadLabelFilters;
  enabled?: boolean;
  useListingPrices?: boolean;
};

type UseWorkloadsMetadataQuery = {
  isLoading: boolean;
  workloads?: WorkloadMetadata[];
  error?: unknown;
  refetch: () => void;
};

const getWorkloadsMetadataQueryOptions = ({
  range: { from, to },
  filter = {},
  enabled = true,
  useListingPrices = false,
}: UseWorkloadsMetadataQueryArgs) =>
  queryOptions({
    queryKey: getWorkloadsMetadataQueryKey({
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      filter: JSON.stringify(filter),
      useListingPrices,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getWorkloadsMetadata(
        {
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          filters: filter,
          useListingPrices,
        },
        { timeout: 30_000 }
      );

      return data.workloads;
    },
    enabled,
  });

export const useWorkloadsMetadataQuery = (
  props: UseWorkloadsMetadataQueryArgs
): UseWorkloadsMetadataQuery => {
  const {
    isLoading,
    data: workloads,
    error,
    refetch,
  } = useQuery(getWorkloadsMetadataQueryOptions(props));

  return {
    workloads,
    isLoading,
    error,
    refetch,
  };
};
